import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { requirePermission } from '@/utils/auth'
import { getAllVehiclesOptions, getRequirementSubintervalsOptions, getAllBillingPeriodsOptions } from '@/api/main'
import { useSuspenseQuery, useSuspenseQueries, useQueryClient } from '@tanstack/react-query'
import { Suspense, useMemo } from 'react'
import { RouteLoading } from '@/components/Common/RouteLoader'
import { invalidateQueriesById } from '@/lib/queryClient'
import EditDriveModalContent from '@/components/Scheduling/EditDriveModal'
import NewDriveModalContent from '@/components/Scheduling/NewDriveModal'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import i18next from 'i18next'

dayjs.extend(utc)
dayjs.extend(timezone)

type ErrorRouteState = {
  errorMessage: string
}

const ERRORS = {
  INVALID_ACTION: i18next.t('invalidActionMessage', { ns: 'scheduling' }),
  MISSING_ID: i18next.t('missingRequiredId', { ns: 'scheduling' }),
  INVALID_VEHICLE_ID: i18next.t('invalidVehicleId', { ns: 'scheduling' }),
  INVALID_DRIVE_ID: i18next.t('invalidDriveId', { ns: 'scheduling' }),
}

const createErrorState = (errorMessage: string): ErrorRouteState => ({
  errorMessage,
})

const navigateToError = (
  navigate: ReturnType<typeof useNavigate>,
  params: { startDate: string; endDate: string },
  errorMessage: string
) =>
  navigate({
    to: '/scheduling/$startDate/$endDate/error',
    params,
    state: createErrorState(errorMessage) as unknown as Parameters<typeof navigate>[0]['state'],
    replace: true,
  })

export const invalidateSchedulerQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  invalidateQueriesById(queryClient, [
    'getRequirementSubintervals',
    'getDetailedDriveStatistics',
    'getScheduleForInterval',
  ])
}

/**
 * Find which billing period a drive belongs to based on its interval dates
 */
const findBillingPeriodForDrive = (
  driveStartAfter: string,
  driveFinishBefore: string,
  billingPeriods: Array<{ id: number; dates: { starting_from: string; ending_at: string } }>
): number | null => {
  const driveStart = dayjs.utc(driveStartAfter).tz('Europe/Prague')
  const driveEnd = dayjs.utc(driveFinishBefore).tz('Europe/Prague')

  for (const period of billingPeriods) {
    const periodStart = dayjs.utc(period.dates.starting_from).tz('Europe/Prague')
    const periodEnd = dayjs.utc(period.dates.ending_at).tz('Europe/Prague')

    // Check if drive overlaps with billing period
    if (driveStart.isBefore(periodEnd) && driveEnd.isAfter(periodStart)) {
      return period.id
    }
  }

  return null
}

export const Route = createFileRoute('/scheduling/$startDate/$endDate/drive/$action/$id')({
  beforeLoad: () => requirePermission('scheduling'),
  component: DriveRouteComponent,
  errorComponent: DriveRouteErrorComponent,
})

function DriveRouteErrorComponent({ error }: { error: Error }) {
  const { startDate, endDate } = Route.useParams()
  const navigate = useNavigate()

  if (error.message.includes('404') || error.message.includes('not found')) {
    navigateToError(navigate, { startDate, endDate }, ERRORS.INVALID_DRIVE_ID)
    return null
  }

  throw error
}

function DriveRouteComponent() {
  const { startDate, endDate, action, id } = Route.useParams()
  const navigate = useNavigate()

  if (action !== 'new' && action !== 'edit') {
    navigateToError(navigate, { startDate, endDate }, ERRORS.INVALID_ACTION)
    return null
  }

  if (!id || id.trim() === '') {
    navigateToError(navigate, { startDate, endDate }, ERRORS.MISSING_ID)
    return null
  }

  const handleClose = () => {
    navigate({
      to: '/scheduling/$startDate/$endDate',
      params: { startDate, endDate },
    })
  }

  if (action === 'new') {
    return (
      <Suspense fallback={<RouteLoading title={i18next.t('loading', { ns: 'common' })} />}>
        <NewDriveRoute vehicleId={id} onClose={handleClose} />
      </Suspense>
    )
  }

  return (
    <Suspense fallback={<RouteLoading title={i18next.t('loading', { ns: 'common' })} />}>
      <EditDriveRoute driveId={id} startDate={startDate} endDate={endDate} onClose={handleClose} />
    </Suspense>
  )
}

function NewDriveRoute({ vehicleId, onClose }: { vehicleId: string; onClose: () => void }) {
  const navigate = useNavigate()
  const { startDate, endDate } = Route.useParams()

  const { data: vehicles } = useSuspenseQuery(getAllVehiclesOptions())

  if (!vehicles.some(v => v.id === vehicleId)) {
    navigateToError(navigate, { startDate, endDate }, ERRORS.INVALID_VEHICLE_ID)
    return null
  }

  return <NewDriveModalContent vehicleId={vehicleId} startDate={startDate} endDate={endDate} onClose={onClose} />
}

function EditDriveRoute({
  driveId,
  startDate,
  endDate,
  onClose,
}: {
  driveId: string
  startDate: string
  endDate: string
  onClose: () => void
}) {
  const navigate = useNavigate()

  // Fetch all billing periods to determine which one the drive belongs to
  const { data: billingPeriods } = useSuspenseQuery(getAllBillingPeriodsOptions())

  // Fetch requirementSubintervals for all overlapping billing periods
  const decodedStartDate = decodeURIComponent(startDate)
  const decodedEndDate = decodeURIComponent(endDate)

  // Find overlapping billing periods
  const overlappingPeriods = useMemo(() => {
    const start = dayjs.utc(decodedStartDate).tz('Europe/Prague')
    const end = dayjs.utc(decodedEndDate).tz('Europe/Prague')

    return billingPeriods.filter(period => {
      const periodStart = dayjs.utc(period.dates.starting_from).tz('Europe/Prague')
      const periodEnd = dayjs.utc(period.dates.ending_at).tz('Europe/Prague')
      return periodStart.isBefore(end) && periodEnd.isAfter(start)
    })
  }, [billingPeriods, decodedStartDate, decodedEndDate])

  // Fetch requirementSubintervals for all overlapping periods
  const requirementSubintervalsQueries = useMemo(
    () =>
      overlappingPeriods.map(period =>
        getRequirementSubintervalsOptions({
          path: {
            billing_period_id: period.id,
            starting_from: decodedStartDate,
            ending_at: decodedEndDate,
          },
        })
      ),
    [overlappingPeriods, decodedStartDate, decodedEndDate]
  )

  const requirementSubintervalsResults = useSuspenseQueries({
    queries: requirementSubintervalsQueries,
    combine: results => results.map(result => result.data),
  })

  if (isNaN(Number(driveId))) {
    navigateToError(navigate, { startDate, endDate }, ERRORS.INVALID_DRIVE_ID)
    return null
  }

  // Find the drive across all results
  const drive = requirementSubintervalsResults
    .flatMap(result => result.drive_subintervals.flatMap(interval => interval.drives.map(drive => drive)))
    .find(drive => drive.id === Number(driveId))

  if (!drive) {
    navigateToError(navigate, { startDate, endDate }, ERRORS.INVALID_DRIVE_ID)
    return null
  }

  // Determine which billing period this drive belongs to
  const driveBillingPeriodId = findBillingPeriodForDrive(drive.start_after, drive.finish_before, billingPeriods)

  if (!driveBillingPeriodId) {
    navigateToError(navigate, { startDate, endDate }, ERRORS.INVALID_DRIVE_ID)
    return null
  }

  return (
    <EditDriveModalContent
      billingPeriodId={driveBillingPeriodId}
      startDate={startDate}
      endDate={endDate}
      onClose={onClose}
      drive={drive}
    />
  )
}
